import { useState } from 'react'
import { Outlet } from 'react-router-dom'
import {
  BookOpen,
  CreditCard,
  FileCheck,
  Gift,
  GraduationCap,
  Home,
  LogOut,
  Settings,
  Trophy,
} from 'lucide-react'
import { useAuthStore } from '@/store/auth.store'
import { UserRole } from '@/types'
import { ROUTES } from '@/constants'
import { AuthLogo } from './auth-logo'
import { Sidebar, type SidebarItem } from './sidebar'
// import { Header } from './header'

const PARENT_ACTIVE_CLASS = 'bg-amber-50 text-[#F28C28]'

export const Layout: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const { user, logout } = useAuthStore()

  const getSidebarItems = (): SidebarItem[] => {
    if (!user) return []

    const parentItems: SidebarItem[] = [
      {
        path: ROUTES.PARENT.DASHBOARD,
        label: 'Dashboard',
        icon: <Home className="w-5 h-5 shrink-0" />,
        activeClassName: PARENT_ACTIVE_CLASS,
      },
      {
        path: ROUTES.PARENT.COURSES,
        label: 'Courses',
        icon: <BookOpen className="w-5 h-5 shrink-0" />,
        activeClassName: PARENT_ACTIVE_CLASS,
      },
      {
        path: ROUTES.PARENT.ENROLLMENTS,
        label: 'Enrollments',
        icon: <GraduationCap className="w-5 h-5 shrink-0" />,
        activeClassName: PARENT_ACTIVE_CLASS,
      },
      {
        path: ROUTES.PARENT.REWARDS,
        label: 'Rewards',
        icon: <Gift className="w-5 h-5 shrink-0" />,
        activeClassName: PARENT_ACTIVE_CLASS,
      },
      {
        path: ROUTES.PARENT.PAYMENTS,
        label: 'Payments',
        icon: <CreditCard className="w-5 h-5 shrink-0" />,
        activeClassName: PARENT_ACTIVE_CLASS,
      },
      {
        path: ROUTES.PARENT.LEADERBOARD,
        label: 'Leaderboard',
        icon: <Trophy className="w-5 h-5 shrink-0" />,
        activeClassName: PARENT_ACTIVE_CLASS,
      },
      {
        path: ROUTES.PARENT.CERTIFICATES,
        label: 'Certificates',
        icon: <FileCheck className="w-5 h-5 shrink-0" />,
        activeClassName: PARENT_ACTIVE_CLASS,
      },
      {
        path: ROUTES.PARENT.SETTINGS,
        label: 'Settings',
        icon: <Settings className="w-5 h-5 shrink-0" />,
        activeClassName: PARENT_ACTIVE_CLASS,
      },
      {
        path: '',
        label: 'Logout',
        icon: <LogOut className="w-5 h-5 shrink-0" />,
        isLogout: true,
      },
    ]

    if (user.role === UserRole.PARENT) return parentItems

    const roleRoutes: Record<UserRole, SidebarItem[]> = {
      [UserRole.ADMIN]: [
        { path: '/admin/dashboard', label: 'Dashboard' },
        { path: '/admin/users', label: 'Users' },
        { path: '/admin/students', label: 'Students' },
        { path: '/admin/teachers', label: 'Teachers' },
        { path: '/admin/classes', label: 'Classes' },
        { path: '/admin/attendance', label: 'Attendance' },
        { path: '/admin/grades', label: 'Grades' },
        { path: '/admin/reports', label: 'Reports' },
      ],
      [UserRole.TEACHER]: [
        { path: '/teacher/dashboard', label: 'Dashboard' },
        { path: '/teacher/classes', label: 'Classes' },
        { path: '/teacher/students', label: 'Students' },
        { path: '/teacher/attendance', label: 'Attendance' },
        { path: '/teacher/grades', label: 'Grades' },
        { path: '/teacher/assignments', label: 'Assignments' },
      ],
      [UserRole.STUDENT]: [
        { path: '/student/dashboard', label: 'Dashboard' },
        { path: '/student/classes', label: 'Classes' },
        { path: '/student/attendance', label: 'Attendance' },
        { path: '/student/grades', label: 'Grades' },
        { path: '/student/assignments', label: 'Assignments' },
        { path: '/student/schedule', label: 'Schedule' },
      ],
      [UserRole.PARENT]: parentItems,
    }

    return roleRoutes[user.role] || []
  }

  const sidebarBrand =
    user?.role === UserRole.PARENT
      ? {
          logo: (
            <AuthLogo
              className="h-10 w-20 object-contain"
              alt="CODE Ambassadors"
            />
          ),
          subtitle: 'Parent Portal',
        }
      : undefined

  return (
    <div className="flex h-screen bg-gray-100">
      <Sidebar
        items={getSidebarItems()}
        brand={sidebarBrand}
        onLogout={logout}
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        {/* <Header onMenuClick={() => setSidebarOpen(true)} /> */}
        <main className="flex-1 overflow-y-auto p-4 sm:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  )
}
